import { type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes } from 'react';

import { cn } from './cn';

/**
 * Поля вводу.
 *
 * До цього кожен екран писав свій набір класів у константі `FIELD` або
 * `CONTROL` — двадцять файлів із трьома різними висотами й межами. Тут одна
 * форма на всіх, з тими самими станами фокуса й помилки, що й решта контролів.
 */
const base =
	'w-full rounded-control border bg-surface text-fg placeholder:text-fg-subtle transition-colors ' +
	'focus:outline-none focus:border-primary focus-visible:ring-2 focus-visible:ring-primary/25 ' +
	'disabled:cursor-not-allowed disabled:opacity-60';

const sizes = { sm: 'h-8 px-2.5 text-xs', md: 'h-9 px-3 text-sm', lg: 'h-11 px-3 text-base' } as const;

export function Input({
	className,
	invalid,
	size = 'md',
	...props
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> & {
	invalid?: boolean;
	size?: keyof typeof sizes;
}): ReactNode {
	return (
		<input
			{...props}
			aria-invalid={invalid || props['aria-invalid']}
			className={cn(base, sizes[size], invalid ? 'border-danger' : 'border-border-strong', className)}
		/>
	);
}

export function Textarea({
	className,
	invalid,
	rows = 3,
	...props
}: TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }): ReactNode {
	return (
		<textarea
			{...props}
			rows={rows}
			aria-invalid={invalid || props['aria-invalid']}
			className={cn(base, 'px-3 py-2 text-sm', invalid ? 'border-danger' : 'border-border-strong', className)}
		/>
	);
}
